import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNow } from 'date-fns';
import { ChevronDown, MoreHorizontal, KeyRound, Plus } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';

import { useAppSelector } from '@common-reducers/hooks/store.hook';
import {
  useListApiKeysQuery,
  ApiKeyRow,
  ApiKeyUsage,
} from '@common-reducers/backend-api/backend-api';
import { selectUserObject } from '@common-reducers/UserSelectors';
import { namespace } from '@common/config/constants';

import { SectionEyebrow } from './section-eyebrow';
import { CreateApiKeyDialog } from './create-api-key-dialog';
import { dialogWorkspaceId } from './dialog-workspace-id';
import { RenameApiKeyDialog } from './rename-api-key-dialog';
import { RevokeApiKeyDialog } from './revoke-api-key-dialog';
import { accessOfScopes } from './api-key-access';
import { GuideVideoButton } from '@/common/components/guide-video/guide-video-button';

/** Keys above this count collapse into the "show more" accordion. */
const VISIBLE_KEY_LIMIT = 2;

function formatLastUsed(iso: string | null, neverLabel: string): string {
  if (!iso) return neverLabel;
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return iso;
  }
}

function renderKeyPrefix(row: ApiKeyRow): string {
  return `bt_${row.environment}_${row.keyPrefix}…`;
}

/** Usage bar turns amber past this fill ratio, and danger once fully spent. */
const USAGE_WARN_RATIO = 0.75;

/**
 * Compact countdown parts for the "resets in 1h 58m" label.
 * Returns null when `iso` isn't a usable timestamp, so the caller can drop the
 * reset clause instead of rendering "Invalid Date".
 */
function getResetParts(iso: string): { hours: number; minutes: number } | null {
  const target = new Date(iso).getTime();
  if (Number.isNaN(target)) return null;
  const remainingMs = target - Date.now();
  if (remainingMs <= 0) return { hours: 0, minutes: 0 };
  const totalMinutes = Math.ceil(remainingMs / 60000);
  return { hours: Math.floor(totalMinutes / 60), minutes: totalMinutes % 60 };
}

/**
 * Compact per-card API usage: `{used}/{limit} requests · resets in 1h 58m`,
 * a slim fill bar, and a "Rate limited" badge once the budget is spent.
 *
 * The quota is keyed on the ACCOUNT, not the key, so we surface that
 * explicitly — otherwise users mint a second key expecting a fresh budget.
 * A missing `usage` means "unknown" and renders nothing.
 */
function ApiKeyUsageLine({ usage }: { usage?: ApiKeyUsage }) {
  const { t } = useTranslation(namespace);

  if (!usage) return null;

  if (usage.unlimited) {
    return (
      <div className="mt-2 text-xs text-[var(--color-text-muted)]">
        {t('api.keys.usage.unlimited')}
      </div>
    );
  }

  const limit = Math.max(0, usage.limit ?? 0);
  const used = Math.max(0, usage.used ?? 0);
  const ratio = limit > 0 ? Math.min(used / limit, 1) : 0;
  const spent = usage.rateLimited || (limit > 0 && used >= limit);
  const barColor = spent
    ? 'bg-[var(--color-danger)]'
    : ratio >= USAGE_WARN_RATIO
      ? 'bg-amber-500'
      : 'bg-[var(--color-accent)]';

  const reset = getResetParts(usage.resetAt);
  const resetLabel =
    reset === null
      ? null
      : reset.hours > 0
        ? t('api.keys.usage.resetHm', {
            hours: reset.hours,
            minutes: reset.minutes,
          })
        : reset.minutes > 0
          ? t('api.keys.usage.resetM', { minutes: reset.minutes })
          : t('api.keys.usage.resetSoon');

  const isAccountWide = usage.keyedOn === 'account';

  const summary = (
    <span className="cursor-default text-xs text-[var(--color-text-muted)]">
      {resetLabel
        ? t('api.keys.usage.lineWithReset', { used, limit, reset: resetLabel })
        : t('api.keys.usage.line', { used, limit })}
    </span>
  );

  return (
    <div className="mt-2 space-y-1.5">
      <div className="flex items-center gap-2">
        {isAccountWide ? (
          <Tooltip>
            <TooltipTrigger asChild>{summary}</TooltipTrigger>
            <TooltipContent>
              {t('api.keys.usage.accountWideHint')}
            </TooltipContent>
          </Tooltip>
        ) : (
          summary
        )}

        {usage.rateLimited && (
          <Badge
            variant="destructive"
            className="text-[10px] uppercase tracking-wide"
          >
            {t('api.keys.usage.rateLimitedBadge')}
          </Badge>
        )}
      </div>

      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={limit}
        aria-valuenow={Math.min(used, limit)}
        aria-label={t('api.keys.usage.barLabel')}
        className="h-1 w-full overflow-hidden rounded-full bg-[var(--color-border)]"
      >
        <div
          className={`h-full rounded-full transition-all ${barColor}`}
          style={{ width: `${ratio * 100}%` }}
        />
      </div>

      {isAccountWide && usage.rateLimited && (
        <p className="text-[11px] text-[var(--color-text-muted)]">
          {t('api.keys.usage.accountWideHint')}
        </p>
      )}
    </div>
  );
}

export function ApiKeysPanel() {
  const { t } = useTranslation(namespace);
  const user = useAppSelector(selectUserObject);
  const workspaceId = user?.currentWorkspace as string | undefined;
  // #431: remember the last id so a transient undefined currentWorkspace can't unmount the open dialog.
  const lastWorkspaceId = useRef<string | undefined>(workspaceId);
  if (workspaceId) lastWorkspaceId.current = workspaceId;
  const createDialogWorkspaceId = dialogWorkspaceId(workspaceId, lastWorkspaceId.current);

  const { data: keys = [], isLoading } = useListApiKeysQuery(
    workspaceId ? { workspaceId } : (undefined as any),
    { skip: !workspaceId },
  );

  const [createOpen, setCreateOpen] = useState(false);
  const [renameRow, setRenameRow] = useState<ApiKeyRow | null>(null);
  const [revokeRow, setRevokeRow] = useState<ApiKeyRow | null>(null);
  const [expanded, setExpanded] = useState(false);

  const sorted = useMemo(
    () => [...keys].sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1)),
    [keys],
  );

  const empty = !isLoading && sorted.length === 0;
  const primaryKeys = sorted.slice(0, VISIBLE_KEY_LIMIT);
  const overflowKeys = sorted.slice(VISIBLE_KEY_LIMIT);

  const renderRow = (row: ApiKeyRow) => (
    <div
      key={row._id}
      className="px-4 py-3 transition-colors hover:bg-[var(--color-surface-muted)]"
    >
      <div className="flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate font-medium text-[var(--color-text)]">
              {row.name}
            </span>
            <Badge
              variant="outline"
              className="text-[10px] uppercase tracking-wide"
              data-testid="api-key-access-badge"
            >
              {t(`api.keys.access.${accessOfScopes(row.scopes)}`)}
            </Badge>
            {row.isLegacy && (
              <Badge
                variant="secondary"
                className="text-[10px] uppercase tracking-wide"
              >
                {t('api.keys.legacyBadge')}
              </Badge>
            )}
          </div>
          <Tooltip>
            <TooltipTrigger asChild>
              <code className="cursor-default font-mono text-xs text-[var(--color-text-muted)]">
                {renderKeyPrefix(row)}
              </code>
            </TooltipTrigger>
            <TooltipContent>{t('api.keyShownOnceTooltip')}</TooltipContent>
          </Tooltip>
        </div>

        <span className="shrink-0 text-xs text-[var(--color-text-muted)]">
          {row.lastUsedAt ? (
            formatLastUsed(row.lastUsedAt, t('api.keys.neverUsed'))
          ) : (
            <span className="italic">{t('api.keys.neverUsed')}</span>
          )}
        </span>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0"
              aria-label={t('api.keys.columnActions')}
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => setRenameRow(row)}>
              {t('api.keys.actions.rename')}
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() => setRevokeRow(row)}
              className="text-destructive"
            >
              {t('api.keys.actions.revoke')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <ApiKeyUsageLine usage={row.usage} />
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <SectionEyebrow>{t('api.keys.sectionLabel')}</SectionEyebrow>
        {!empty && (
          <Button
            onClick={() => setCreateOpen(true)}
            disabled={!workspaceId}
            className="h-8 shrink-0 gap-1.5 border-transparent bg-[var(--color-accent)] text-white hover:bg-[var(--color-accent)]/90"
          >
            <Plus className="h-4 w-4" />
            {t('api.keys.createButton')}
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="divide-y divide-[var(--color-border)] overflow-hidden rounded-lg border border-[var(--color-border)]">
          {[0, 1].map((i) => (
            <div key={i} className="flex items-center gap-4 p-3">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-4 w-40" />
              <Skeleton className="ms-auto h-4 w-20" />
            </div>
          ))}
        </div>
      ) : empty ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-[var(--color-border)] py-12 text-center">
          <KeyRound className="mb-3 h-10 w-10 text-[var(--color-text-muted)]" />
          <p className="mb-4 text-sm text-[var(--color-text-muted)]">
            {t('api.keys.empty')}
          </p>
          <Button
            variant="outline"
            onClick={() => setCreateOpen(true)}
            disabled={!workspaceId}
            className="gap-2"
          >
            <Plus className="h-4 w-4" />
            {t('api.keys.createButton')}
          </Button>
          <GuideVideoButton lesson="api" variant="link" surface="api_keys_empty" style={{ marginTop: 12 }} />
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-[var(--color-border)]">
          <div className="divide-y divide-[var(--color-border)]">
            {primaryKeys.map(renderRow)}
          </div>

          {overflowKeys.length > 0 && (
            <Collapsible open={expanded} onOpenChange={setExpanded}>
              <CollapsibleContent>
                <div className="divide-y divide-[var(--color-border)] border-t border-[var(--color-border)]">
                  {overflowKeys.map(renderRow)}
                </div>
              </CollapsibleContent>
              <CollapsibleTrigger asChild>
                <button
                  type="button"
                  className="flex w-full items-center justify-center gap-1.5 border-t border-[var(--color-border)] bg-[var(--color-surface-muted)] py-2.5 text-xs font-medium text-[var(--color-text-muted)] transition-colors hover:text-[var(--color-text)]"
                >
                  <ChevronDown
                    className={`h-4 w-4 transition-transform ${
                      expanded ? 'rotate-180' : ''
                    }`}
                  />
                  {expanded
                    ? t('api.keys.showLess')
                    : t('api.keys.showMore', { count: overflowKeys.length })}
                </button>
              </CollapsibleTrigger>
            </Collapsible>
          )}
        </div>
      )}

      {createDialogWorkspaceId && (
        <CreateApiKeyDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          workspaceId={createDialogWorkspaceId}
        />
      )}
      <RenameApiKeyDialog
        row={renameRow}
        onOpenChange={(o) => {
          if (!o) setRenameRow(null);
        }}
      />
      <RevokeApiKeyDialog
        row={revokeRow}
        onOpenChange={(o) => {
          if (!o) setRevokeRow(null);
        }}
      />
    </div>
  );
}

export default ApiKeysPanel;
